/**
 * Majaku Privacy Background Service
 * 
 * Provides a Three.js-based 3D background scene that can be used as a selectable background.
 * Same as the loading screen variant but without the logo overlay.
 */

import { state as appState, updateState } from '../../core/state.js';
import { bus } from '../../core/events.js';
import { createCameraRig, NavCameraMotion, IdleCameraMotion, type IntroEffectType } from './cameraRig.js';
import { setupScene } from './scene/sceneSetup.js';
import { updateCortexNetwork } from './scene/cortexNetwork.js';
import { createNetworkActivityParticles } from './network/networkVisualization.js';
import { createNetworkRequestParticles } from './network/networkRequestVisualization.js';
import { setupNetworkEventHandlers, type NetworkActivityState } from './network/events.js';
import type { PathGenerationOptions } from './network/connectionPaths.js';
import { updateRibbons } from './scene/ribbon.js';
import { updatePipes } from './scene/pipes.js';
import { updateTestArrow } from './scene/testArrow.js';
import { DARK_THEME, DAY_THEME, type MajakuTheme } from './theme.js';
import { createSkyShader, updateSkyShader } from './scene/sky-shader.js';

// Re-export enums and types for backwards compatibility
export { NavCameraMotion, IdleCameraMotion };
export type { IntroEffectType };

export interface MajakuPrivacyBackgroundState {
  canvas: HTMLCanvasElement | null;
  initialized: boolean;
  isActive: boolean;
  theme: 'dark' | 'day';
}

let state: MajakuPrivacyBackgroundState = {
  canvas: null,
  initialized: false,
  isActive: false,
  theme: 'day',
};

let renderer: any = null;
let scene: any = null;
let camera: any = null;
let birdsEyeCamera: any = null; // Separate bird's eye view camera
let birdsEyeMode: boolean = false; // Flag to track bird's eye view mode
// minimal post process (for barrel↔pincushion)
let rtScene: any = null;
let rtCamera: any = null;
let rtQuad: any = null;
let renderTarget: any = null;
let controls: any = null;
let THREE: any = null; // Store THREE module for use in animation
let particles: any = null;
const ribbonPlanes: any[] = [];
let cameraRig: ReturnType<typeof createCameraRig> | null = null;
// Pipe curve references
let upPipeCurve: any = null;
let downPipeCurve: any = null;
let extendedPipeLength: number = 0;
let pipesVisible: boolean = false;
// Transform groups for each pipe to parent lines to
let upPipeGroup: any = null;
let downPipeGroup: any = null;
// Test arrow at center
let testArrow: any = null;
// Gradient sphere
let gradientSphere: any = null;
// Cortex network visualization
let cortexNetwork: any = null;
// UI sphere in top right corner (rendered to screen-space quad)
let uiSphere: any = null;
let uiSphereLogoPlane: any = null;
let uiSphereRenderTarget: any = null; // Small render target for sphere
let uiSphereQuad: any = null; // Screen-space quad for compositing
let uiSphereQuadScene: any = null; // Separate scene for UI sphere quad
let uiSphereScene: any = null; // Separate scene for sphere rendering (isolated from main scene)
let uiSphereCamera: any = null; // Dedicated camera for sphere rendering
let marbleSystemUpdateFunctions: { updateMarbleMaterial: any; updateMarbleRotation: any } | null = null; // Cached marble system functions
let uiSphereParticle: any = null; // Particle sprite inside sphere for state visualization
let currentMicrophoneState: string = 'idle'; // Current microphone state
let microphoneStateListeners: Array<() => void> = []; // Event listeners for state tracking
// Cached dynamic imports to avoid expensive imports every frame
let cachedUpdateSkySphereAnimations: ((deltaTime: number) => void) | null = null;
let cachedUpdateScatteredLights: ((state: any, currentTime: number, minDistance: number, maxDistance: number) => void) | null = null;
let cachedRenderBackgroundForRefraction: ((state: any, THREE: any, renderer: any, scene: any, camera: any) => void) | null = null;
let cachedUpdateCortexCamera: ((state: any, THREE: any, camera: any, deltaTime: number, cameraRig?: any) => void) | null = null;
let cachedUpdateMetaball: ((state: any, deltaTime: number, uiSphere?: any, camera?: any, backgroundTexture?: any, THREE?: any, microphoneState?: any) => void) | null = null;
// Click interaction state for sphere
let sphereClickState: {
  mouseDownTime: number | null;
  wasListeningBeforeMouseDown: boolean;
  wasQuickClick: boolean;
  isButtonHeld: boolean;
} = {
  mouseDownTime: null,
  wasListeningBeforeMouseDown: false,
  wasQuickClick: false,
  isButtonHeld: false,
};
const TOGGLE_CLICK_MAX_DURATION_MS = 500; // < 500ms = toggle click
// Pipe parameters for network visualization routing
let mainPipeRadius = 5;
let smallPipeRadius = 2;
let smallSphereY = -8;
let animationId: number | null = null;
let resizeHandler: (() => void) | null = null;
let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
let mouseMoveHandler: ((e: MouseEvent) => void) | null = null;

// Mouse tracking for camera
let mouseX = 0;
let mouseY = 0;
let targetLookAtX = 0;
let targetLookAtY = 0;
const mouseLerpSpeed = 0.02;
const maxLookAtOffset = 3;

// Network visualization systems
let networkViz: ReturnType<typeof createNetworkActivityParticles> | null = null;
let networkRequestViz: ReturnType<typeof createNetworkRequestParticles> | null = null;
let networkActivityState: NetworkActivityState | null = null;

// Theme system
let currentTheme: MajakuTheme = DAY_THEME;
let dayGroup: any = null;
let waterPlane: any = null;
let skyShader: any = null;
let mainAmbientLight: any = null;
let mainDirectionalLight: any = null;

// Gradient lerping state
let gradientLerpState: {
  startGradient: any;
  targetGradient: any;
  startTime: number;
  duration: number;
  isLerping: boolean;
} | null = null;
let keyboardHandler: ((e: KeyboardEvent) => void) | null = null;

// -------------------------------------------------------
// Gradient keyboard controls
// -------------------------------------------------------

function setupGradientKeyboardControls(): void {
  if (keyboardHandler) {
    return; // Already set up
  }

  keyboardHandler = (e: KeyboardEvent) => {
    if (!state.isActive || !gradientSphere || !gradientSphere.material) {
      return;
    }

    // Dynamic import to avoid circular dependency
    import('./scene/jsPlacementTowerConfig.js').then(({ TOWER_CONFIG }) => {
      const gradients = TOWER_CONFIG.skyboxGradients;
      if (!gradients || gradients.length === 0) {
        return;
      }

      let newIndex = TOWER_CONFIG.activeSkyboxGradientIndex;

      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();

        if (e.key === 'ArrowUp') {
          newIndex = (TOWER_CONFIG.activeSkyboxGradientIndex + 1) % gradients.length;
        } else {
          newIndex = (TOWER_CONFIG.activeSkyboxGradientIndex - 1 + gradients.length) % gradients.length;
        }

        if (newIndex !== TOWER_CONFIG.activeSkyboxGradientIndex) {
          TOWER_CONFIG.activeSkyboxGradientIndex = newIndex;
          // Persist the selected index to localStorage
          try {
            localStorage.setItem('majaku-privacy-skybox-gradient-index', String(newIndex));
          } catch (error) {
          }
          startGradientLerp(gradients[newIndex]);
          // Update base gradient in animation system
          import('./scene/skySphereAnimations.js').then(({ updateBaseGradient }) => {
            updateBaseGradient(gradients[newIndex]);
          });
        }
      }
    });
  };

  window.addEventListener('keydown', keyboardHandler);
}

function startGradientLerp(targetGradient: any): void {
  if (!gradientSphere || !gradientSphere.material || !gradientSphere.material.uniforms) {
    return;
  }

  const uniforms = gradientSphere.material.uniforms;
  const startGradient = {
    blueHue: uniforms.uBlueHue.value,
    blueSaturation: uniforms.uBlueSaturation.value,
    blueLightness: uniforms.uBlueLightness.value,
    whiteR: uniforms.uWhite.value.x,
    whiteG: uniforms.uWhite.value.y,
    whiteB: uniforms.uWhite.value.z,
    blackR: uniforms.uBlack.value.x,
    blackG: uniforms.uBlack.value.y,
    blackB: uniforms.uBlack.value.z,
    threshold1: uniforms.uThreshold1.value,
    threshold2: uniforms.uThreshold2.value,
    threshold3: uniforms.uThreshold3.value,
  };

  gradientLerpState = {
    startGradient,
    targetGradient,
    startTime: performance.now(),
    duration: 2000, // 2 seconds
    isLerping: true,
  };
}

function updateGradientLerp(currentTime: number): void {
  if (!gradientLerpState || !gradientLerpState.isLerping || !gradientSphere || !gradientSphere.material || !gradientSphere.material.uniforms) {
    return;
  }

  const { startGradient, targetGradient, startTime, duration } = gradientLerpState;
  const elapsed = currentTime - startTime;
  const progress = Math.min(elapsed / duration, 1.0);

  // Ease in-out cubic for smooth transition
  const easeInOutCubic = (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  const t = easeInOutCubic(progress);

  // Lerp all gradient values
  const uniforms = gradientSphere.material.uniforms;
  uniforms.uBlueHue.value = startGradient.blueHue + (targetGradient.blueHue - startGradient.blueHue) * t;
  uniforms.uBlueSaturation.value = startGradient.blueSaturation + (targetGradient.blueSaturation - startGradient.blueSaturation) * t;
  uniforms.uBlueLightness.value = startGradient.blueLightness + (targetGradient.blueLightness - startGradient.blueLightness) * t;
  uniforms.uWhite.value.x = startGradient.whiteR + (targetGradient.whiteR - startGradient.whiteR) * t;
  uniforms.uWhite.value.y = startGradient.whiteG + (targetGradient.whiteG - startGradient.whiteG) * t;
  uniforms.uWhite.value.z = startGradient.whiteB + (targetGradient.whiteB - startGradient.whiteB) * t;
  uniforms.uBlack.value.x = startGradient.blackR + (targetGradient.blackR - startGradient.blackR) * t;
  uniforms.uBlack.value.y = startGradient.blackG + (targetGradient.blackG - startGradient.blackG) * t;
  uniforms.uBlack.value.z = startGradient.blackB + (targetGradient.blackB - startGradient.blackB) * t;
  uniforms.uThreshold1.value = startGradient.threshold1 + (targetGradient.threshold1 - startGradient.threshold1) * t;
  uniforms.uThreshold2.value = startGradient.threshold2 + (targetGradient.threshold2 - startGradient.threshold2) * t;
  uniforms.uThreshold3.value = startGradient.threshold3 + (targetGradient.threshold3 - startGradient.threshold3) * t;

  if (progress >= 1.0) {
    gradientLerpState.isLerping = false;
  }
}

// -------------------------------------------------------
// Navigation camera gestures
// -------------------------------------------------------

export function triggerNavMotion(m: NavCameraMotion) {
  if (cameraRig) {
    cameraRig.triggerNav(m);
  }
}

// -------------------------------------------------------
// Idle camera movement modes
// -------------------------------------------------------

export function setIdleCameraMotion(mode: IdleCameraMotion) {
  // This function is kept for backwards compatibility but now updates state
  import('../../core/state.js').then(({ updateState }) => {
    updateState('ui', (draft: any) => {
      if (!draft) draft = { selection: null, fps: 0 };
      draft.idleCameraMotion = mode;
    });
  });
}

// -------------------------------------------------------
// Bird's eye view camera routine
// -------------------------------------------------------

/**
 * Toggle bird's eye view camera mode
 * When active, switches to a separate bird's eye view camera that looks down from above
 * The main camera remains unperturbed in this mode
 */
export function toggleBirdsEyeView(): boolean {
  birdsEyeMode = !birdsEyeMode;
  return birdsEyeMode;
}

/**
 * Set bird's eye view camera mode
 */
export function setBirdsEyeView(enabled: boolean): void {
  birdsEyeMode = enabled;
}

/**
 * Get current bird's eye view mode
 */
export function getBirdsEyeView(): boolean {
  return birdsEyeMode;
}

/**
 * Initialize the majaku-privacy background scene
 */
export async function initializeMajakuPrivacyBackground(
  container: HTMLElement
): Promise<MajakuPrivacyBackgroundState> {
  if (state.initialized && state.canvas) {
    return state;
  }

  // Create canvas for Three.js rendering
  const canvas = document.createElement('canvas');
  canvas.id = 'majaku-privacy-background-canvas';
  canvas.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -2;
    pointer-events: none;
    display: none;
    background-color: #000000;
  `;
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;

  container.appendChild(canvas);

  try {
    // Add import map for Three.js modules to resolve "three" imports
    if (!document.querySelector('script[type="importmap"]')) {
      const importMap = document.createElement('script');
      importMap.type = 'importmap';
      importMap.textContent = JSON.stringify({
        imports: {
          'three': 'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.js',
          'three/addons/': 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/'
        }
      });
      document.head.appendChild(importMap);
      // Wait a bit for import map to be processed
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // @ts-expect-error - CDN import, resolved at runtime
    THREE = await import('https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.js') as any;
    // @ts-expect-error - CDN import, resolved at runtime
    const { OrbitControls } = await import('https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/controls/OrbitControls.js') as any;

    const {
      Scene,
      PerspectiveCamera,
      WebGLRenderer,
      PCFSoftShadowMap,
      OrthographicCamera,
      WebGLRenderTarget,
      PlaneGeometry,
      Vector2,
      Mesh,
      ShaderMaterial,
    } = THREE as any;

    scene = new Scene();

    camera = new PerspectiveCamera(90, window.innerWidth / window.innerHeight, 0.1, 10000); // Increased FOV from 75 to 90
    const radius = 200; // Reduced from 25 to bring camera closer to metaball (makes it appear larger)
    camera.position.set(radius, 0, 0);

    // Create bird's eye view camera (OrthographicCamera positioned above looking down)
    const birdsEyeHeight = 50; // Height above the scene
    const birdsEyeSize = 40; // Size of the view area
    const aspect = window.innerWidth / window.innerHeight;
    birdsEyeCamera = new OrthographicCamera(
      -birdsEyeSize * aspect,
      birdsEyeSize * aspect,
      birdsEyeSize,
      -birdsEyeSize,
      0.1,
      10000
    );
    birdsEyeCamera.position.set(0, birdsEyeHeight, 0);
    birdsEyeCamera.lookAt(0, 0, 0);
    birdsEyeCamera.up.set(0, 0, 1); // Set up vector along Z-axis for proper top-down view

    // Create camera rig
    cameraRig = createCameraRig({
      THREE,
      camera,
      getIdleMode: () =>
        (appState.ui?.idleCameraMotion as IdleCameraMotion) ||
        IdleCameraMotion.PendulumOrbit,
      getMouseLookOffset: () => ({ x: targetLookAtX, y: targetLookAtY }),
    });

    // CRITICAL: Add powerPreference for MacBooks - forces high-performance GPU instead of low-power mode
    // Also add failIfMajorPerformanceCaveat: false to allow fallback if needed
    renderer = new WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
      powerPreference: "high-performance", // Force high-performance GPU on MacBooks
      failIfMajorPerformanceCaveat: false, // Allow fallback if high-performance not available
      preserveDrawingBuffer: false, // Better performance (don't preserve frame buffer)
      stencil: false, // Disable stencil buffer if not needed (better performance)
      depth: true // Keep depth buffer for 3D rendering
    });
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    renderer.setPixelRatio(pixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = PCFSoftShadowMap;
    renderer.sortObjects = true;
    renderer.outputColorSpace = (THREE as any).SRGBColorSpace || undefined;
    renderer.setClearColor(0x000000, 1);

    // Additional performance hints for MacBooks
    // Disable auto-clear to reduce overhead (we manually clear when needed)
    renderer.autoClear = true; // Keep auto-clear enabled for now, but we can optimize later

    // Verify WebGL context was created successfully and log GPU info
    const gl = renderer.getContext() as WebGLRenderingContext;
    if (!gl) {
      // Failed to get WebGL context! This will cause severe performance issues.
    } else {
      // Check if we got a high-performance context
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        const rendererName = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);

        // Warn if using software rendering (Intel HD Graphics on MacBooks might indicate software fallback)
        if (rendererName && (rendererName.includes('Software') || rendererName.includes('SwiftShader'))) {
          // WARNING: Using software rendering! This will cause severe performance issues.
          // Try: Chrome -> chrome://flags -> "Override software rendering list" -> Enabled
        }
      }

      // Check if we have WebGL2 (better performance)
      const isWebGL2 = gl instanceof WebGL2RenderingContext;
    }

    controls = new OrbitControls(camera, canvas);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enabled = false;

    // === Scene Setup ===
    mainPipeRadius = 5;
    smallPipeRadius = 2;
    smallSphereY = -8;
    const cameraDistance = radius;
    extendedPipeLength = cameraDistance * 2;

    const pipeConfig = {
      mainPipeRadius,
      smallPipeRadius,
      smallSphereY,
      extendedPipeLength,
      cameraDistance,
    };

    const sceneSetupResult = setupScene(scene, THREE, pipeConfig, 'bezier');
    particles = sceneSetupResult.particles;
    ribbonPlanes.push(...sceneSetupResult.ribbonPlanes);
    upPipeCurve = sceneSetupResult.pipes.upPipeCurve;
    downPipeCurve = sceneSetupResult.pipes.downPipeCurve;
    upPipeGroup = sceneSetupResult.pipes.upPipeGroup;
    downPipeGroup = sceneSetupResult.pipes.downPipeGroup;
    testArrow = sceneSetupResult.testArrow;
    gradientSphere = sceneSetupResult.gradientSphere;
    cortexNetwork = sceneSetupResult.cortexNetwork;

    // Store sceneSetupResult for access to scatteredLights and metaball
    (window as any).__majakuSceneSetupResult = sceneSetupResult;

    // === Day Scene Setup ===
    // Create day group for day scene elements
    const { Group } = THREE;
    dayGroup = new Group();
    dayGroup.visible = false; // Start hidden (default is dark)
    scene.add(dayGroup);

    // Create day elements
    // Ribbons
    import('./scene/parametric-ribbons.js').then(({ createParametricRibbons }) => {
      const ribbonGroup = createParametricRibbons(scene, THREE);
      scene.remove(ribbonGroup);
      dayGroup.add(ribbonGroup);
    });

    // Water
    import('./scene/water-plane.js').then(({ createWaterPlane }) => {
      waterPlane = createWaterPlane(scene, THREE);
      // waterPlane is already added to scene in createWaterPlane (via scene.add(group))
      // We need to move it to dayGroup
      scene.remove(waterPlane);
      dayGroup.add(waterPlane);
    });

    // Sky Shader (Scene background replacement)
    skyShader = createSkyShader(scene, THREE);

    // Load saved theme from localStorage settings before applying
    try {
      const savedSettings = localStorage.getItem('pivot-settings');
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        if (parsed?.preferences?.backgroundTheme === 'dark' || parsed?.preferences?.backgroundTheme === 'day') {
          const savedTheme = parsed.preferences.backgroundTheme;
          currentTheme = savedTheme === 'day' ? DAY_THEME : DARK_THEME;
          state.theme = savedTheme;
        } else {
        }
      } else {
      }
    } catch (e) {
      // If settings can't be loaded, use default (already set to DAY_THEME)
    }

    // Apply theme immediately after all elements are created
    // Ensure sky shader is configured BEFORE first render
    updateSkyShader(skyShader, 0, currentTheme.name);
    // Explicitly set visibility based on current theme to ensure it's visible on initial load
    const shouldBeVisible = currentTheme.name === 'day';
    skyShader.visible = shouldBeVisible;

    // Find lights to control them
    scene.traverse((object: any) => {
      if (object.isAmbientLight) mainAmbientLight = object;
      if (object.isDirectionalLight) mainDirectionalLight = object;
    });

    // Apply theme immediately after all elements are created
    applyTheme(currentTheme);

    // Helper function to create UI sphere in main scene, rendered to screen-space quad
    function createUISphereScreenSpace(THREE: any, cortexNetwork: any, mainScene: any, mainRenderer: any): void {

      // Import marble system to power the sphere
      import('./sphere/marbleSystem.js').then(({
        createMarbleMaterial,
        DEFAULT_MARBLE_PARAMS,
        updateMarbleMaterial,
        updateMarbleRotation
      }) => {
        if (!createMarbleMaterial) {
          return;
        }

        try {
          const {
            PerspectiveCamera,
            SphereGeometry,
            PlaneGeometry,
            ShaderMaterial,
            Vector3,
            Vector2,
            Matrix4,
            DoubleSide,
            WebGLRenderTarget,
            OrthographicCamera,
            Mesh
          } = THREE;

          // Store marble parameters - using custom configuration
          const marbleParams: any = {
            rippleSpeed: 0.13,
            rippleAmplitude: 0.042,
            rippleFrequency: 1.5,
            iorRed: 2,
            iorGreen: 1.53,
            iorBlue: 1.54,
            refractOffset: 0.5,
            baseAlpha: 0.16,
            fresnelPower: 2.6,
            fresnelIntensity: 0.03,
            specularPower: 256,
            specularIntensity: 2,
            fresnelColor: { r: 0.9, g: 0.95, b: 1 },
            rotYSpeed: 0.0082,
            rotXSpeed: 0.0063,
            marbleRadius: 1.5, // Restored to original size
            marbleSegments: 64,
          };

          const sphereSize = 600; // Render target size in pixels (matches quad pixel size)

          // Create small render target for sphere
          uiSphereRenderTarget = new WebGLRenderTarget(sphereSize, sphereSize, {
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter,
            format: THREE.RGBAFormat,
            stencilBuffer: false,
          });

          // Create separate scene for sphere rendering (completely isolated from main scene)
          const { Scene } = THREE;
          uiSphereScene = new Scene();

          // Add lighting to match main scene
          import('./scene/lighting.js').then(({ setupLighting }) => {
            setupLighting(uiSphereScene, THREE);
          });

          // Add gradient sphere skybox to match main scene
          import('./scene/gradientSphere.js').then(({ createGradientSphere }) => {
            import('./scene/jsPlacementTowerConfig.js').then(({ TOWER_CONFIG }) => {
              const gradients = TOWER_CONFIG.skyboxGradients;
              const savedIndex = TOWER_CONFIG.activeSkyboxGradientIndex;
              const validIndex = (savedIndex >= 0 && savedIndex < gradients.length) ? savedIndex : 0;
              const activeGradient = gradients[validIndex] || gradients[0];
              createGradientSphere(uiSphereScene, THREE, TOWER_CONFIG.skyboxScale, activeGradient);
            });
          });

          // Create camera that will copy the main camera's transform
          // This ensures the sphere sees the same environment from the camera's perspective
          uiSphereCamera = new PerspectiveCamera(50, 1, 0.1, 1000);
          // Position will be updated each frame to match main camera

          // Create empty Group instead of sphere mesh - just for transform tracking
          // The metaball in the main scene will copy this transform
          const { Group } = THREE;
          uiSphere = new Group();
          uiSphere.position.set(0, 0, 0);
          uiSphereScene.add(uiSphere);

          // Create metaball in uiSphereScene (for composite rendering)
          import('./scene/metaball.js').then(({ createMetaball }) => {
            // Get background texture from cortex network for refraction
            const backgroundTexture = cortexNetwork?.backgroundRenderTarget?.texture || null;
            const metaball = createMetaball(
              uiSphereScene,
              THREE,
              backgroundTexture,
              uiSphereCamera,
              { x: sphereSize, y: sphereSize }
            );
            // Store metaball for updates
            (window as any).__majakuSceneSetupResult = (window as any).__majakuSceneSetupResult || {};
            (window as any).__majakuSceneSetupResult.metaball = metaball;

            // TEST: Apply current metaball configuration after 10 seconds
            setTimeout(() => {
              const currentState = metaball.currentProfileState || 'idle';
              import('./scene/metaball.js').then(({ switchMetaballProfile }) => {
                switchMetaballProfile(metaball, currentState as any).then(() => {
                }).catch((error) => {
                });
              });
            }, 10000); // 10 seconds
          });

          // Create particle sprite inside sphere for state visualization (like cortex network nodes)
          import('./sphere/microphoneStates.js').then(({ createStateParticleTexture, MICROPHONE_STATES }) => {
            const { Sprite, SpriteMaterial } = THREE;

            // Create particle texture with idle state color
            const idleState = MICROPHONE_STATES.idle;
            const particleTexture = createStateParticleTexture(THREE, idleState.particleColor);

            const particleMaterial = new SpriteMaterial({
              map: particleTexture,
              transparent: true,
              opacity: idleState.flashOffOpacity,
              blending: THREE.AdditiveBlending,
              depthWrite: false,
            });

            uiSphereParticle = new Sprite(particleMaterial);
            const particleSize = marbleParams.marbleRadius * 0.4; // Proportional to sphere size
            uiSphereParticle.scale.set(particleSize, particleSize, 1);
            uiSphereParticle.position.set(0, 0, 0); // Center of sphere
            uiSphereScene.add(uiSphereParticle);

            // Store particle in scene for updates
            (uiSphereScene as any).particle = uiSphereParticle;
            (uiSphereScene as any).particleTexture = particleTexture;
            (uiSphereScene as any).particleMaterial = particleMaterial;

            // Set up event listeners for microphone state changes
            setupMicrophoneStateListeners();

            // Set up click handlers for sphere interaction
            setupSphereClickHandlers();
          });

          // Create screen-space quad for compositing (positioned in top-right corner)
          const quadPixelSize = 600; // Fixed pixel size (600x600)
          // Create square geometry
          const quadGeometry = new PlaneGeometry(2.0, 2.0);

          // Shader for screen-space quad positioned in top-right corner
          // Uses absolute pixel dimensions (600x600)
          const quadVertexShader = `
            uniform vec2 uResolution;
            uniform float uQuadSize;
            varying vec2 vUv;
            void main() {
              vUv = uv;
              // Convert pixel size to NDC (normalized device coordinates)
              // NDC ranges from -1 to 1, so pixel size / resolution gives us the size in NDC
              float quadSizeNDC = uQuadSize / uResolution.y; // Use height for consistent sizing
              float quadWidthNDC = uQuadSize / uResolution.x;
              float quadHeightNDC = uQuadSize / uResolution.y;
              
              // Position quad in top-right corner
              // Screen space: x=[-1,1], y=[-1,1], origin at center
              vec2 pos = position.xy;
              // Scale to desired pixel size
              pos.x = pos.x * quadWidthNDC;
              pos.y = pos.y * quadHeightNDC;
              // Position in top-right corner (with some padding)
              pos.x = pos.x + (1.0 - quadWidthNDC - 0.02); // 0.02 = 2% padding
              pos.y = pos.y + (1.0 - quadHeightNDC - 0.02);
              gl_Position = vec4(pos, 0.0, 1.0);
            }
          `;

          const quadFragmentShader = `
            uniform sampler2D uSphereTexture;
            varying vec2 vUv;
            void main() {
              vec4 color = texture2D(uSphereTexture, vUv);
              // Discard fully transparent/black pixels to avoid showing black background
              if (color.a < 0.01) {
                discard;
              }
              gl_FragColor = color;
            }
          `;

          const quadMaterial = new ShaderMaterial({
            vertexShader: quadVertexShader,
            fragmentShader: quadFragmentShader,
            uniforms: {
              uSphereTexture: { value: uiSphereRenderTarget.texture },
              uResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
              uQuadSize: { value: quadPixelSize },
            },
            transparent: true,
            depthTest: false,
            depthWrite: false,
          });

          // Create separate scene for UI sphere quad (rendered after post-processing)
          uiSphereQuadScene = new THREE.Scene();
          uiSphereQuad = new Mesh(quadGeometry, quadMaterial);
          uiSphereQuadScene.add(uiSphereQuad);

        } catch (error) {
          if (error instanceof Error) {
          }
        }
      }).catch((error) => {
      });
    }

    // Initialize render target for cortex network refraction (PS2-style)
    if (cortexNetwork && renderer) {
      import('./scene/cortexNetwork.js').then(({ initializeCortexNetworkRenderTarget }) => {
        if (initializeCortexNetworkRenderTarget) {
          initializeCortexNetworkRenderTarget(cortexNetwork, THREE, renderer, camera);

          // Create UI sphere in main scene with screen-space compositing after render target is ready
          createUISphereScreenSpace(THREE, cortexNetwork, scene, renderer);
        }
      });
    }

    // === Network Visualization Setup ===
    networkActivityState = {
      userSpeaking: false,
      aiResponding: false,
      userSpeechTimeout: null,
      aiResponseTimeout: null,
    };

    const pathOptions: PathGenerationOptions = {
      upPipeCurve,
      downPipeCurve,
      mainPipeRadius,
      smallPipeRadius,
      upPipeGroup,
      downPipeGroup,
      THREE,
    };

    networkViz = createNetworkActivityParticles(
      scene,
      camera,
      particles,
      pathOptions
    );

    setupNetworkEventHandlers(networkActivityState, (type, isStartup) => {
      const currentTime = performance.now() * 0.001;
      networkViz!.createConnection(currentTime, type, isStartup);
    });

    networkRequestViz = createNetworkRequestParticles(
      scene,
      camera,
      pathOptions
    );

    // === Post-Process Setup ===
    (function setupPost() {
      renderTarget = new WebGLRenderTarget(window.innerWidth, window.innerHeight, {
        depthBuffer: true,
      });
      rtScene = new Scene();
      rtCamera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
      const fsQuadGeo = new PlaneGeometry(2, 2);
      const postFrag = `
        uniform sampler2D uTex;
        uniform float uK;        // distortion (barrel<0, pincushion>0)
        uniform vec2  uRes;      // resolution
        varying vec2 vUv;
        void main() {
          vec2 uv = vUv * 2.0 - 1.0;               // [-1,1]
          float r2 = dot(uv, uv);
          // Simple radial distortion model: uv' = uv * (1 + k*r^2)
          float f = 1.0 + uK * r2;
          vec2 distorted = uv * f;
          vec2 samp = (distorted * 0.5) + 0.5;
          
          // Minor chromatic aberration for texture
          // Offset RGB channels slightly for color fringing effect
          float aberrationAmount = 0.002; // Small offset for subtle effect
          vec2 offset = normalize(distorted) * aberrationAmount * r2;
          
          float r = texture2D(uTex, clamp(samp + offset, 0.0, 1.0)).r;
          float g = texture2D(uTex, clamp(samp, 0.0, 1.0)).g;
          float b = texture2D(uTex, clamp(samp - offset, 0.0, 1.0)).b;
          
          // vignette soft clamp to reduce edge stretching artifacts
          if (any(lessThan(samp, vec2(0.0))) || any(greaterThan(samp, vec2(1.0)))){
            gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
          } else {
            gl_FragColor = vec4(r, g, b, 1.0);
          }
        }
      `;
      const postVert = `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position, 1.0);
        }
      `;
      const postMat = new ShaderMaterial({
        vertexShader: postVert,
        fragmentShader: postFrag,
        uniforms: {
          uTex: { value: null },
          uK: { value: 0.0 },
          uRes: { value: new Vector2(window.innerWidth, window.innerHeight) },
        },
        depthTest: false,
        depthWrite: false,
      });
      rtQuad = new Mesh(fsQuadGeo, postMat);
      rtScene.add(rtQuad);
    })();

    // Debounce resize handler
    resizeHandler = () => {
      if (!renderer || !camera || !canvas) return;

      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }

      resizeTimeout = setTimeout(() => {
        const rect = canvas.getBoundingClientRect();
        const width = rect.width || window.innerWidth;
        const height = rect.height || window.innerHeight;

        camera.aspect = width / height;
        camera.updateProjectionMatrix();

        // Update bird's eye camera aspect ratio
        if (birdsEyeCamera) {
          const aspect = width / height;
          const birdsEyeSize = 40;
          birdsEyeCamera.left = -birdsEyeSize * aspect;
          birdsEyeCamera.right = birdsEyeSize * aspect;
          birdsEyeCamera.top = birdsEyeSize;
          birdsEyeCamera.bottom = -birdsEyeSize;
          birdsEyeCamera.updateProjectionMatrix();
        }

        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        renderer.setPixelRatio(pixelRatio);
        renderer.setSize(width, height, false);

        if (renderTarget) {
          renderTarget.setSize(width * pixelRatio, height * pixelRatio);
        }

        if (rtQuad && rtQuad.material && rtQuad.material.uniforms?.uRes) {
          rtQuad.material.uniforms.uRes.value.set(width, height);
        }

        // Update UI sphere overlay canvas size on resize (fixed size, no change needed)

        if (canvas.width !== width * pixelRatio || canvas.height !== height * pixelRatio) {
          canvas.width = width * pixelRatio;
          canvas.height = height * pixelRatio;
        }
      }, 100);
    };

    window.addEventListener('resize', resizeHandler);

    if (!scene || !camera || !renderer) {
      throw new Error('Failed to create Three.js scene, camera, or renderer');
    }

    state = {
      canvas,
      initialized: true,
      isActive: false,
      theme: state.theme,
    };

    return state;
  } catch (error) {
    throw error;
  }
}

/**
 * Privacy activation effect - creates a subtle cyberspace activation animation
 * Simulates network requests, responses, LLM prompts, and microphone activity
 * with far fewer emissions for a subtle activation effect
 */
function privacyActivationEffect(): void {

  // Simulate fewer network requests/responses for subtle activation effect
  const numEvents = 10; // Far fewer events - just enough for a subtle effect

  // First, update the activeNetworkRequests state so the white visualization will create lines
  updateState('ui', (draft: any) => {
    if (!draft) draft = { selection: null, fps: 0 };
    draft.activeNetworkRequests = numEvents * 2; // Simulate active requests
  });

  // After a delay, reduce the count back to 0
  setTimeout(() => {
    updateState('ui', (draft: any) => {
      if (!draft) draft = { selection: null, fps: 0 };
      draft.activeNetworkRequests = 0;
    });
  }, 3000); // Keep active for 3 seconds (shorter duration)

  // Schedule network.request.started events (outgoing)
  for (let i = 0; i < numEvents; i++) {
    setTimeout(() => {
      const payload = {
        requestId: `privacy-activation-request-${i}-${Date.now()}`,
        timestamp: Date.now()
      };
      bus.emit('network.request.started', payload);
    }, i * 100); // 100ms between each (slower spread)
  }

  // Schedule network.request.started events (incoming/responses)
  for (let i = 0; i < numEvents; i++) {
    setTimeout(() => {
      const payload = {
        requestId: `privacy-activation-response-${i}-${Date.now()}`,
        timestamp: Date.now()
      };
      bus.emit('network.request.started', payload);
    }, i * 100 + 150); // Offset by 150ms
  }

  // Schedule drawing.aiCommandExecuted events (LLM prompts) - with _test flag
  for (let i = 0; i < numEvents; i++) {
    setTimeout(() => {
      const payload = {
        command: { type: 'simulated', id: `privacy-activation-llm-${i}-${Date.now()}` },
        _test: true // Flag to indicate this is a test event
      };
      bus.emit('drawing.aiCommandExecuted', payload);
    }, i * 100 + 300); // Offset by 300ms
  }

  // Schedule user.voice events (microphone) - with _test flag
  for (let i = 0; i < numEvents; i++) {
    setTimeout(() => {
      const payload = {
        transcript: `privacy activation voice ${i}`,
        timestamp: Date.now(),
        duration_ms: 1000,
        _test: true // Flag to indicate this is a test event
      };
      bus.emit('user.voice', payload);
    }, i * 100 + 450); // Offset by 450ms
  }

}

/**
 * Activate the majaku-privacy background
 */
export function activateMajakuPrivacyBackground(): void {
  if (!state.canvas) {
    setTimeout(() => activateMajakuPrivacyBackground(), 100);
    return;
  }

  if (!state.initialized) {
    setTimeout(() => activateMajakuPrivacyBackground(), 100);
    return;
  }

  if (!renderer || !scene || !camera) {
    setTimeout(() => activateMajakuPrivacyBackground(), 100);
    return;
  }

  state.isActive = true;
  state.canvas.style.display = 'block';

  if (resizeHandler) {
    resizeHandler();
  }

  const storedIntro = localStorage.getItem('majaku-privacy-intro-effect') as IntroEffectType | null;
  const defaultIntro: IntroEffectType = 'barrel-to-pincushion';
  const chosen =
    storedIntro &&
      ['dolly-zoom', 'fov-ramp', 'push-in-fov-expansion', 'barrel-to-pincushion', 'dive-up', 'dive-down'].includes(
        storedIntro
      )
      ? storedIntro
      : defaultIntro;

  if (cameraRig) {
    cameraRig.startIntro(chosen);
  }

  // Set up mouse tracking for camera and cortex network hover
  if (!mouseMoveHandler) {
    mouseMoveHandler = (e: MouseEvent) => {
      const normalizedX = ((e.clientX / window.innerWidth) - 0.5) * 2;
      const normalizedY = ((e.clientY / window.innerHeight) - 0.5) * 2;

      mouseX = normalizedX * maxLookAtOffset * 0.2;
      mouseY = -normalizedY * maxLookAtOffset * 0.2;

      // Handle cortex network hover
      if (cortexNetwork && state.canvas && camera && !cortexNetwork.cameraState.isZoomed) {
        import('./scene/cortexNetwork.js').then(({ handleCortexNetworkHover }) => {
          if (state.canvas) {
            handleCortexNetworkHover(cortexNetwork, THREE, e.clientX, e.clientY, camera, state.canvas);
          }
        });
      }
    };
    window.addEventListener('mousemove', mouseMoveHandler);

    // Set up click handler for cortex network zoom
    const clickHandler = (e: MouseEvent) => {
      if (cortexNetwork && state.canvas && camera) {
        import('./scene/cortexNetwork.js').then(({ handleCortexNetworkClick }) => {
          if (state.canvas) {
            handleCortexNetworkClick(cortexNetwork, THREE, e.clientX, e.clientY, camera, state.canvas);
          }
        });
      }
    };
    window.addEventListener('click', clickHandler);

    // Store handler for cleanup
    (window as any).__cortexClickHandler = clickHandler;
  }


  // Set up keyboard handlers for gradient cycling
  setupGradientKeyboardControls();

  // Trigger privacy activation effect immediately when entering privacy theme
  privacyActivationEffect();

  // Start animation if not already running
  if (animationId === null) {
    const startAnimation = () => {
      if (!renderer || !scene || !camera) {
        setTimeout(startAnimation, 50);
        return;
      }

      let lastFrameTime = performance.now();

      // CRITICAL: Check if document is visible - browsers throttle RAF when tab is hidden
      // This helps MacBooks maintain performance when tab is visible
      const isVisible = () => {
        if (typeof document.hidden !== 'undefined') {
          return !document.hidden;
        }
        return true; // Assume visible if API not available
      };

      const animate = () => {
        if (!state.isActive) {
          if (animationId) {
            cancelAnimationFrame(animationId);
            animationId = null;
          }
          return;
        }

        // Skip frame if tab is hidden (browser will throttle anyway)
        if (!isVisible()) {
          animationId = requestAnimationFrame(animate);
          return;
        }

        animationId = requestAnimationFrame(animate);

        renderer.autoClear = true;
        renderer.setClearColor(0x000000, 1);

        const currentTime = performance.now() * 0.001;
        const deltaTime = (performance.now() - lastFrameTime) / 1000; // Convert to seconds
        lastFrameTime = performance.now();

        // Update gradient lerp if active
        updateGradientLerp(performance.now());

        // Update sky sphere animations (cache import to avoid expensive dynamic import every frame)
        if (!cachedUpdateSkySphereAnimations) {
          import('./scene/skySphereAnimations.js').then(({ updateSkySphereAnimations }) => {
            cachedUpdateSkySphereAnimations = updateSkySphereAnimations;
            cachedUpdateSkySphereAnimations(deltaTime);
          });
        } else {
          cachedUpdateSkySphereAnimations(deltaTime);
        }

        // Update scattered FAA lights with atmospheric perspective (cache import)
        const sceneSetupResult = (window as any).__majakuSceneSetupResult;
        if (sceneSetupResult?.scatteredLights) {
          if (!cachedUpdateScatteredLights) {
            import('./scene/scatteredLights.js').then(({ updateScatteredLights }) => {
              cachedUpdateScatteredLights = updateScatteredLights;
              cachedUpdateScatteredLights(sceneSetupResult.scatteredLights, currentTime, 300, 1500);
            });
          } else {
            cachedUpdateScatteredLights(sceneSetupResult.scatteredLights, currentTime, 300, 1500);
          }
        }

        // Check if cortex network is zoomed in (this takes priority over idle camera)
        const isCortexZoomed = cortexNetwork?.cameraState?.isZoomed || false;

        // Check if there's an active zoom-out transition (zoomStartTime is set but isZoomed is false)
        const isZoomingOut = cortexNetwork?.cameraState?.zoomStartTime !== null && !isCortexZoomed;

        // Update camera via rig (only if not in bird's eye mode, not cortex zoomed, and not zooming out)
        let distortionK = 0;
        if (!birdsEyeMode && !isCortexZoomed && !isZoomingOut && cameraRig) {
          const res = cameraRig.update(currentTime);
          distortionK = res.distortionK;
        }

        // Smoothly interpolate look-at target toward mouse position (only if not in bird's eye mode, not cortex zoomed, and not zooming out)
        if (!birdsEyeMode && !isCortexZoomed && !isZoomingOut) {
          targetLookAtX += (mouseX - targetLookAtX) * mouseLerpSpeed;
          targetLookAtY += (mouseY - targetLookAtY) * mouseLerpSpeed;
        }

        // Update ribbons
        updateRibbons(ribbonPlanes, currentTime);

        // Update network visualization
        if (networkViz) {
          networkViz.updateConnections(currentTime);
        }

        // Update network request visualization
        if (networkRequestViz) {
          networkRequestViz.updateNetworkRequestLines(currentTime);
        }

        // Render background to texture for refractive mapping (PS2-style) (cache import)
        if (cortexNetwork && renderer && scene && camera && !birdsEyeMode) {
          if (!cachedRenderBackgroundForRefraction) {
            import('./scene/cortexNetwork.js').then(({ renderBackgroundForRefraction }) => {
              cachedRenderBackgroundForRefraction = renderBackgroundForRefraction;
              cachedRenderBackgroundForRefraction(cortexNetwork, THREE, renderer, scene, camera);
            });
          } else {
            cachedRenderBackgroundForRefraction(cortexNetwork, THREE, renderer, scene, camera);
          }
        }

        // Update cortex network visualization (pulsing animations)
        if (cortexNetwork && THREE) {
          updateCortexNetwork(cortexNetwork, THREE, deltaTime, camera, scene);

          // Update camera zoom/orbit if a cortex is selected OR if zooming out (this overrides idle camera) (cache import)
          if (camera && !birdsEyeMode && (isCortexZoomed || isZoomingOut)) {
            if (!cachedUpdateCortexCamera) {
              import('./scene/cortexNetwork.js').then(({ updateCortexCamera }) => {
                cachedUpdateCortexCamera = updateCortexCamera;
                cachedUpdateCortexCamera(cortexNetwork, THREE, camera, deltaTime, cameraRig);
              });
            } else {
              cachedUpdateCortexCamera(cortexNetwork, THREE, camera, deltaTime, cameraRig);
            }
          }
        }

        // Update test arrow to point at camera - pipes will automatically follow since they're parented
        // Only update if not in bird's eye mode (use main camera for arrow tracking)
        if (testArrow && !birdsEyeMode) {
          updateTestArrow(testArrow, camera, THREE);
        }

        // Update metaball animation (copy transform from UI sphere) (cache import)
        if (sceneSetupResult?.metaball && uiSphere && uiSphereCamera) {
          if (!cachedUpdateMetaball) {
            import('./scene/metaball.js').then(({ updateMetaball }) => {
              cachedUpdateMetaball = updateMetaball;
              // Get background texture from cortex network for refraction
              const backgroundTexture = cortexNetwork?.backgroundRenderTarget?.texture || null;
              // Pass current microphone state to auto-switch profiles
              cachedUpdateMetaball(
                sceneSetupResult.metaball,
                deltaTime,
                uiSphere,
                uiSphereCamera,
                backgroundTexture,
                THREE,
                currentMicrophoneState as any // Pass microphone state for profile switching
              );
            });
          } else {
            // Get background texture from cortex network for refraction
            const backgroundTexture = cortexNetwork?.backgroundRenderTarget?.texture || null;
            // Pass current microphone state to auto-switch profiles
            cachedUpdateMetaball(
              sceneSetupResult.metaball,
              deltaTime,
              uiSphere,
              uiSphereCamera,
              backgroundTexture,
              THREE,
              currentMicrophoneState as any // Pass microphone state for profile switching
            );
          }
        }

        // Render UI sphere to render target (screen-space compositing)
        // Use completely isolated scene - no conflicts with main scene
        if (uiSphere && uiSphereScene && uiSphereRenderTarget && uiSphereCamera && !birdsEyeMode) {
          // Offset sphere camera up and to the right to mimic real 3D position (not composite)
          // Calculate viewport-relative offset in world space
          const sphereDistance = 5; // Fixed distance from camera
          const fovRad = (camera.fov * Math.PI) / 180;
          const aspect = camera.aspect;
          const heightAtDistance = 2 * Math.tan(fovRad / 2) * sphereDistance;
          const widthAtDistance = heightAtDistance * aspect;

          // Offset to top-right (40% from center to edge)
          const rightOffset = widthAtDistance * 0.4;
          const upOffset = heightAtDistance * 0.4;

          // Calculate camera local axes
          const cameraRight = new THREE.Vector3(1, 0, 0);
          const cameraUp = new THREE.Vector3(0, 1, 0);
          const cameraForward = new THREE.Vector3(0, 0, -1);

          // Transform to world space
          cameraRight.applyQuaternion(camera.quaternion);
          cameraUp.applyQuaternion(camera.quaternion);
          cameraForward.applyQuaternion(camera.quaternion);

          // Offset sphere camera position up and to the right
          const offsetSphereCameraPos = camera.position.clone()
            .add(cameraRight.multiplyScalar(rightOffset))
            .add(cameraUp.multiplyScalar(upOffset));

          // Copy main camera's rotation to sphere camera so it sees the same environment
          uiSphereCamera.position.copy(offsetSphereCameraPos);
          uiSphereCamera.quaternion.copy(camera.quaternion);
          uiSphereCamera.updateMatrixWorld(); // Update matrix to ensure transform is applied

          // Keep fixed FOV (reduced to zoom in on metaball)
          uiSphereCamera.fov = 35; // Reduced from 50 to zoom in on metaball (makes it appear larger)
          uiSphereCamera.aspect = 1; // Square aspect for sphere render target
          uiSphereCamera.updateProjectionMatrix();

          // Position empty group at fixed distance forward from the offset camera
          // This makes it appear in the top-right corner as if it's really there
          // The metaball in the main scene will copy this transform
          const sphereWorldPos = offsetSphereCameraPos.clone()
            .add(cameraForward.multiplyScalar(sphereDistance));
          uiSphere.position.copy(sphereWorldPos);

          // Update particle for state visualization (if it exists)
          const particle = (uiSphereScene as any)?.particle;
          const particleMaterial = (uiSphereScene as any)?.particleMaterial;

          if (particle && particleMaterial) {
            // Get current state configuration
            import('./sphere/microphoneStates.js').then(({ MICROPHONE_STATES }) => {
              const stateConfig = MICROPHONE_STATES[currentMicrophoneState as keyof typeof MICROPHONE_STATES] || MICROPHONE_STATES.idle;

              // Update particle sprite based on state
              // Update particle color if state changed
              const currentColor = particleMaterial.color;
              const targetColor = stateConfig.particleColor;
              const targetColorHex = (targetColor.r << 16) | (targetColor.g << 8) | targetColor.b;

              if (currentColor.getHex() !== targetColorHex) {
                // Update texture if color changed
                import('./sphere/microphoneStates.js').then(({ createStateParticleTexture }) => {
                  const newTexture = createStateParticleTexture(THREE, targetColor);
                  particleMaterial.map = newTexture;
                  particleMaterial.needsUpdate = true;

                  // Dispose old texture
                  if ((uiSphereScene as any).particleTexture) {
                    (uiSphereScene as any).particleTexture.dispose();
                  }
                  (uiSphereScene as any).particleTexture = newTexture;
                });
              }

              // Update particle flash pattern (FAA-style)
              const particleBaseSize = 0.6; // Base size for particle (was marbleParams.marbleRadius * 0.4)
              if (stateConfig.flashRate > 0) {
                const flashCycleDuration = 60.0 / stateConfig.flashRate;
                const cycleTime = (currentTime % flashCycleDuration);
                const flashOnDuration = 0.15; // 150ms ON

                if (cycleTime < flashOnDuration) {
                  particleMaterial.opacity = stateConfig.flashOnOpacity;
                  particle.scale.setScalar(particleBaseSize * 1.2);
                } else {
                  particleMaterial.opacity = stateConfig.flashOffOpacity;
                  particle.scale.setScalar(particleBaseSize);
                }
              } else {
                // No flashing - steady state
                particleMaterial.opacity = stateConfig.flashOffOpacity;
                particle.scale.setScalar(particleBaseSize);
              }

              // Gentle floating animation
              const floatPhase = currentTime * 0.5;
              particle.position.y = Math.sin(floatPhase) * 0.1;
            });
          }

          // Render isolated sphere scene to its render target
          // Save current renderer state
          const originalAutoClear = renderer.autoClear;
          const originalRenderTarget = renderer.getRenderTarget();
          const viewportTemp = new THREE.Vector4();
          const scissorTemp = new THREE.Vector4();
          const originalViewport = renderer.getViewport(viewportTemp).clone();
          const originalScissor = renderer.getScissor(scissorTemp).clone();
          const originalScissorTest = renderer.getScissorTest();

          // Hide background objects (gradient sphere skybox, etc.) to show only metaball
          const backgroundVisibilityStates = new Map<any, boolean>();
          uiSphereScene.traverse((child: any) => {
            // Hide gradient sphere skybox specifically (identified by uBlueHue uniform)
            // This keeps metaball, particle, and lights visible
            if (child.isMesh && child.material && child.material.uniforms && child.material.uniforms.uBlueHue !== undefined) {
              // This is the gradient sphere skybox
              backgroundVisibilityStates.set(child, child.visible);
              child.visible = false;
            }
          });

          renderer.autoClear = true;
          renderer.setRenderTarget(uiSphereRenderTarget);
          renderer.setViewport(0, 0, 600, 600); // Match render target size

          // Save and set clear color to transparent for sphere render target
          const originalClearColor = new THREE.Color();
          const originalClearAlpha = renderer.getClearColor(originalClearColor).getHex();
          const originalClearAlphaValue = renderer.getClearAlpha();
          renderer.setClearColor(0x000000, 0); // Clear to transparent black

          renderer.clear();
          renderer.render(uiSphereScene, uiSphereCamera);

          // Restore background object visibility
          backgroundVisibilityStates.forEach((visible, child) => {
            child.visible = visible;
          });

          // Explicitly reset renderer state to defaults
          renderer.setRenderTarget(null);
          renderer.setClearColor(originalClearColor, originalClearAlphaValue); // Restore original clear color
          renderer.setViewport(originalViewport.x, originalViewport.y, originalViewport.z, originalViewport.w);
          renderer.setScissor(originalScissor.x, originalScissor.y, originalScissor.z, originalScissor.w);
          renderer.setScissorTest(originalScissorTest);
          renderer.autoClear = originalAutoClear;
        }

        // Determine which camera to use for rendering
        const activeCamera = birdsEyeMode ? birdsEyeCamera : camera;

        // No need to hide UI sphere/logo - they're in a completely separate scene

        // Render path: always use post-processing for chromatic aberration
        // Note: distortion effects are disabled in bird's eye mode
        const usePost = !birdsEyeMode; // Always use post-processing for chromatic aberration
        if (usePost && renderTarget && rtQuad) {
          // Ensure render target is set up correctly
          const width = renderer.domElement.width || window.innerWidth;
          const height = renderer.domElement.height || window.innerHeight;
          if (renderTarget.width !== width || renderTarget.height !== height) {
            renderTarget.setSize(width, height);
          }

          // Render main scene to render target
          // Ensure renderer state is clean
          const originalAutoClear = renderer.autoClear;
          const originalRenderTarget = renderer.getRenderTarget();
          const viewportTemp = new THREE.Vector4();
          const scissorTemp = new THREE.Vector4();
          const originalViewport = renderer.getViewport(viewportTemp).clone();
          const originalScissor = renderer.getScissor(scissorTemp).clone();
          const originalScissorTest = renderer.getScissorTest();

          renderer.autoClear = true;
          renderer.setRenderTarget(renderTarget);
          renderer.setViewport(0, 0, width, height); // Match render target size
          renderer.clear();

          // Debug: Count visible objects
          let visibleCount = 0;
          scene.traverse((obj: any) => {
            if (obj.visible && obj.type !== 'Scene') visibleCount++;
          });
          if (visibleCount === 0) {
            console.warn('[Render] Warning: No visible objects in scene!');
          }

          renderer.render(scene, camera);

          // Explicitly reset renderer state before post-processing
          renderer.setRenderTarget(null);
          renderer.setViewport(originalViewport.x, originalViewport.y, originalViewport.z, originalViewport.w);
          renderer.setScissor(originalScissor.x, originalScissor.y, originalScissor.z, originalScissor.w);
          renderer.setScissorTest(originalScissorTest);
          renderer.autoClear = originalAutoClear;

          // Update post-processing uniforms
          rtQuad.material.uniforms.uTex.value = renderTarget.texture;
          rtQuad.material.uniforms.uK.value = distortionK;
          rtQuad.material.uniforms.uRes.value.set(width, height);

          // Render post-processing quad (full screen with chromatic aberration)
          // Ensure we're rendering to screen (not a render target)
          if (renderer.getRenderTarget() !== null) {
            renderer.setRenderTarget(null);
          }
          renderer.autoClear = false; // Don't clear - we want to composite on top
          renderer.render(rtScene, rtCamera);

          // Render UI sphere quad on top (after post-processing)
          if (uiSphereQuad && uiSphereQuadScene && uiSphereRenderTarget) {
            // Update resolution uniform for pixel-perfect sizing
            if (uiSphereQuad.material && uiSphereQuad.material.uniforms) {
              if (uiSphereQuad.material.uniforms.uResolution) {
                uiSphereQuad.material.uniforms.uResolution.value.set(width, height);
              }
            }
            renderer.render(uiSphereQuadScene, rtCamera);
          }

          // Handle sphere click interaction (check after rendering)
          handleSphereClickInteraction(width, height);

          // Reset autoClear for next frame
          renderer.autoClear = true;
        } else {
          renderer.render(scene, activeCamera);
        }
      };

      animate();
    };

    startAnimation();
  }
}

/**
 * Set the background theme
 */
export function setTheme(themeName: 'dark' | 'day'): void {

  if (state.theme === themeName) {
    return;
  }

  state.theme = themeName;
  currentTheme = themeName === 'day' ? DAY_THEME : DARK_THEME;

  applyTheme(currentTheme);
}

/**
 * Apply the current theme settings
 */
function applyTheme(theme: MajakuTheme): void {

  if (!scene || !renderer) {
    return;
  }

  // Update Fog
  if (scene.fog) {
    // Smooth transition for fog density? For now instant.
    scene.fog.color.setHex(theme.fog.color);
    scene.fog.density = theme.fog.density;
  }

  // Update Background
  renderer.setClearColor(theme.background, 1);

  // Update Lighting
  if (mainAmbientLight) {
    mainAmbientLight.intensity = theme.lighting.ambientIntensity;
    mainAmbientLight.color.setHex(theme.lighting.ambientColor);
  }

  // If we don't have a directional light (night mode might not have one), create it for day mode
  if (theme.name === 'day' && !mainDirectionalLight && THREE) {
    mainDirectionalLight = new THREE.DirectionalLight(theme.lighting.directionalColor, theme.lighting.directionalIntensity);
    mainDirectionalLight.position.set(
      theme.lighting.directionalPosition.x,
      theme.lighting.directionalPosition.y,
      theme.lighting.directionalPosition.z
    );
    scene.add(mainDirectionalLight);
  } else if (mainDirectionalLight) {
    mainDirectionalLight.intensity = theme.lighting.directionalIntensity;
    mainDirectionalLight.color.setHex(theme.lighting.directionalColor);
    mainDirectionalLight.position.set(
      theme.lighting.directionalPosition.x,
      theme.lighting.directionalPosition.y,
      theme.lighting.directionalPosition.z
    );
  }

  // Show/Hide Geometry Groups
  if (dayGroup) {
    dayGroup.visible = theme.name === 'day';
  }

  // Toggle Night Elements (Ribbon Planes from original scene)
  // We should hide the original ribbon planes in day mode as they clash
  ribbonPlanes.forEach(plane => {
    plane.visible = theme.name === 'dark';
  });

  // Toggle pipes
  if (upPipeGroup) upPipeGroup.visible = theme.name === 'dark';
  if (downPipeGroup) downPipeGroup.visible = theme.name === 'dark';

  // Toggle particles (maybe keep them but lighter?)
  // For now hide them in day mode for a cleaner look
  if (particles) {
    particles.visible = theme.name === 'dark';
  }

  // Update water plane visibility (redundant if in dayGroup but safe)
  if (waterPlane) {
    waterPlane.visible = theme.name === 'day';
  }

  // Update Sky Shader
  if (skyShader) {
    updateSkyShader(skyShader, performance.now() * 0.001, theme.name);
    // If we want to revert to the "void" look for night mode exactly as before,
    // we can hide the skyShader mesh in dark mode.
    // The request said "in the day mode, let's get rid of the traditional sky sphere... replace with shader".
    // It implies night mode might still want the old look or the new shader adapted.
    // Let's assume the new shader can handle night mode too (deep blue/black gradient).
    // But to be safe and preserve the exact old night look:
    const wasVisible = skyShader.visible;
    skyShader.visible = theme.name === 'day';

    // If we hide the shader in night mode, we need to ensure the gradient sphere (old system) 
    // is visible in night mode and hidden in day mode.
    if (gradientSphere) {
      gradientSphere.visible = theme.name === 'dark';
    }
  } else {
  }
}

/**
 * Set up event listeners for microphone state tracking
 * Called after sphere is created
 */
function setupMicrophoneStateListeners(): void {
  // Clean up existing listeners
  microphoneStateListeners.forEach(cleanup => cleanup());
  microphoneStateListeners = [];

  // Track state from events
  const updateState = (newState: string) => {
    currentMicrophoneState = newState;
  };

  // Listen for microphone state changes
  const listeningHandler = (payload: unknown) => {
    const data = payload as { isListening: boolean; timestamp: number };
    if (data.isListening) {
      updateState('listening');
    } else {
      updateState('idle');
    }
  };

  const turnedOnHandler = () => {
    updateState('listening');
  };

  const turnedOffHandler = () => {
    updateState('idle');
  };

  const userVoiceHandler = () => {
    updateState('processing');
    // Reset to listening after processing
    setTimeout(() => {
      if (currentMicrophoneState === 'processing') {
        updateState('listening');
      }
    }, 2000);
  };

  const audioLevelHandler = (payload: unknown) => {
    const data = payload as { amplitude: number; timestamp: number };
    if (data.amplitude > 0.7) {
      updateState('highAudio');
    } else if (currentMicrophoneState === 'highAudio') {
      updateState('listening');
    }
  };

  // Listen for AI response (TTS or drawing) - but distinguish proactive from reactive
  let isProactiveSpeaking = false;

  const proactiveMessageHandler = () => {
    isProactiveSpeaking = true;
    updateState('proactiveSpeaking');
  };

  const aiResponseHandler = () => {
    // If this is a proactive message, use proactiveSpeaking state
    if (isProactiveSpeaking) {
      updateState('proactiveSpeaking');
      setTimeout(() => {
        if (currentMicrophoneState === 'proactiveSpeaking') {
          updateState('idle');
          isProactiveSpeaking = false;
        }
      }, 5000); // Proactive messages might be longer
    } else {
      // Regular AI response to user input
      updateState('aiResponding');
      setTimeout(() => {
        if (currentMicrophoneState === 'aiResponding') {
          updateState('idle');
        }
      }, 3000);
    }
  };

  const wakeWordHandler = () => {
    updateState('wakeWord');
    setTimeout(() => {
      if (currentMicrophoneState === 'wakeWord') {
        updateState('listening');
      }
    }, 1000);
  };

  const stopRequestedHandler = () => {
    updateState('idle');
  };

  // Listen for proactive message custom event (from proactive service)
  const proactiveMessageEventListener = () => {
    proactiveMessageHandler();
  };
  window.addEventListener('proactive-message', proactiveMessageEventListener);

  // Register listeners (bus is imported at top of file)
  bus.on('microphone.listening.changed', listeningHandler);
  bus.on('microphone.turned.on', turnedOnHandler);
  bus.on('microphone.turned.off', turnedOffHandler);
  bus.on('user.voice', userVoiceHandler);
  bus.on('microphone.audio.level', audioLevelHandler);
  bus.on('drawing.aiStrokeAdded', aiResponseHandler);
  bus.on('drawing.aiCommandExecuted', aiResponseHandler);
  bus.on('tts.started', aiResponseHandler);
  bus.on('microphone.activate.requested', wakeWordHandler);
  bus.on('microphone.stop.requested', stopRequestedHandler);

  // Store cleanup functions
  microphoneStateListeners.push(() => {
    window.removeEventListener('proactive-message', proactiveMessageEventListener);
    bus.off('microphone.listening.changed', listeningHandler);
    bus.off('microphone.turned.on', turnedOnHandler);
    bus.off('microphone.turned.off', turnedOffHandler);
    bus.off('user.voice', userVoiceHandler);
    bus.off('microphone.audio.level', audioLevelHandler);
    bus.off('drawing.aiStrokeAdded', aiResponseHandler);
    bus.off('drawing.aiCommandExecuted', aiResponseHandler);
    bus.off('tts.started', aiResponseHandler);
    bus.off('microphone.activate.requested', wakeWordHandler);
    bus.off('microphone.stop.requested', stopRequestedHandler);
  });
}

/**
 * Handle click interaction on the sphere (same behavior as original microphone view)
 * Single click = toggle continuous listening
 * Hold click = push-to-talk
 */
function handleSphereClickInteraction(width: number, height: number): void {
  // Sphere is 600x600 pixels in top-right corner
  const sphereSize = 600;
  const padding = 0.02; // 2% padding (matches quad shader)
  const sphereWidthNDC = sphereSize / width;
  const sphereHeightNDC = sphereSize / height;

  // Calculate screen bounds in pixels
  const sphereRight = width - (width * padding);
  const sphereLeft = sphereRight - sphereSize;
  const sphereTop = height - (height * padding);
  const sphereBottom = sphereTop - sphereSize;

  // Store bounds for click detection (will be checked in event handlers)
  (window as any).__sphereClickBounds = {
    left: sphereLeft,
    right: sphereRight,
    top: sphereTop,
    bottom: sphereBottom,
  };
}

/**
 * Check if a click is within the sphere bounds
 */
function isClickInSphere(x: number, y: number): boolean {
  const bounds = (window as any).__sphereClickBounds;
  if (!bounds) return false;

  return x >= bounds.left && x <= bounds.right &&
    y >= bounds.top && y <= bounds.bottom;
}

/**
 * Get microphone process instance to call methods
 */
async function getMicrophoneProcess(): Promise<any> {
  const { processManager } = await import('../../systems/process-manager.js');
  return processManager.getProcess('microphone-main');
}

/**
 * Set up click handlers for sphere interaction
 */
function setupSphereClickHandlers(): void {
  // Remove existing handlers if any
  if ((window as any).__sphereMouseDownHandler) {
    window.removeEventListener('mousedown', (window as any).__sphereMouseDownHandler);
    window.removeEventListener('mouseup', (window as any).__sphereMouseUpHandler);
    window.removeEventListener('click', (window as any).__sphereClickHandler);
  }

  // Mouse down handler
  const mouseDownHandler = async (e: MouseEvent) => {
    if (!isClickInSphere(e.clientX, e.clientY)) return;

    const process = await getMicrophoneProcess();
    if (!process) return;

    // Get current listening state
    const state = process.getState ? process.getState() : { isListening: false };

    // Record the time and state when mouse is pressed down
    sphereClickState.mouseDownTime = Date.now();
    sphereClickState.wasListeningBeforeMouseDown = state.isListening || false;
    sphereClickState.wasQuickClick = false; // Reset flag

    // For push-to-talk: start listening when mouse is pressed down (if not already listening)
    if (!state.isListening && !sphereClickState.isButtonHeld) {
      sphereClickState.isButtonHeld = true;
      if (process.startListening) {
        process.startListening();
      } else {
        // Fallback to event
        bus.emit('microphone.activate.requested', { reason: 'sphere_click', timestamp: Date.now() });
      }
    } else if (state.isListening && !sphereClickState.isButtonHeld) {
      // If already listening, mark button as held for push-to-talk
      sphereClickState.isButtonHeld = true;
    }
  };

  // Mouse up handler
  const mouseUpHandler = async (e: MouseEvent) => {
    if (!isClickInSphere(e.clientX, e.clientY)) {
      // Click outside sphere - reset state
      if (sphereClickState.mouseDownTime) {
        sphereClickState.mouseDownTime = null;
        sphereClickState.isButtonHeld = false;
      }
      return;
    }

    if (!sphereClickState.mouseDownTime) return; // No mousedown recorded, ignore

    const process = await getMicrophoneProcess();
    if (!process) return;

    const duration = Date.now() - sphereClickState.mouseDownTime;
    sphereClickState.wasQuickClick = duration < TOGGLE_CLICK_MAX_DURATION_MS;

    if (sphereClickState.wasQuickClick) {
      // Quick click (< 500ms) = toggle click
      // Don't stop here - let onClick handle the toggle
      sphereClickState.isButtonHeld = false;
      // Keep mouseDownTime so onClick can use it
    } else {
      // Long press (>= 500ms) = push-to-talk: stop listening
      if (sphereClickState.isButtonHeld) {
        const state = process.getState ? process.getState() : { isListening: false };
        if (state.isListening) {
          sphereClickState.isButtonHeld = false;
          if (process.stopListening) {
            process.stopListening();
          } else {
            // Fallback to event
            bus.emit('microphone.stop.requested', { reason: 'sphere_release', timestamp: Date.now() });
          }
        }
      }
      // Mark that this was NOT a quick click, so onClick won't toggle
      sphereClickState.mouseDownTime = null; // Clear so onClick knows it was handled
      sphereClickState.wasQuickClick = false; // Ensure flag is false for long press
    }
  };

  // Click handler (for toggle)
  const clickHandler = async (e: MouseEvent) => {
    if (!isClickInSphere(e.clientX, e.clientY)) return;

    const process = await getMicrophoneProcess();
    if (!process) return;

    // Only handle toggle clicks (quick clicks)
    // Push-to-talk is already handled in mouseup (wasQuickClick is false for long presses)
    if (!sphereClickState.wasQuickClick) {
      // Was a long press or no mousedown recorded - ignore
      return;
    }

    // Quick click = toggle
    // If we started listening in mousedown (was off), we want to keep it on
    // If it was already on, we want to turn it off
    if (sphereClickState.wasListeningBeforeMouseDown) {
      // Was already on - toggle it off
      if (process.toggleListening) {
        process.toggleListening();
      } else {
        // Fallback: stop listening
        if (process.stopListening) {
          process.stopListening();
        } else {
          bus.emit('microphone.stop.requested', { reason: 'sphere_toggle', timestamp: Date.now() });
        }
      }
    } else {
      // Was off, we started it in mousedown - keep it on (don't toggle)
      // It's already on from mousedown, so do nothing
    }
    sphereClickState.isButtonHeld = false;

    // Reset for next interaction
    sphereClickState.mouseDownTime = null;
    sphereClickState.wasQuickClick = false;
  };

  // Register handlers
  window.addEventListener('mousedown', mouseDownHandler);
  window.addEventListener('mouseup', mouseUpHandler);
  window.addEventListener('click', clickHandler);

  // Store for cleanup
  (window as any).__sphereMouseDownHandler = mouseDownHandler;
  (window as any).__sphereMouseUpHandler = mouseUpHandler;
  (window as any).__sphereClickHandler = clickHandler;
}

/**
 * Deactivate the majaku-privacy background
 */
export function deactivateMajakuPrivacyBackground(): void {
  state.isActive = false;

  // Clean up keyboard handler
  if (keyboardHandler) {
    window.removeEventListener('keydown', keyboardHandler);
    keyboardHandler = null;
  }
  if (state.canvas) {
    state.canvas.style.display = 'none';
  }

  if (animationId) {
    cancelAnimationFrame(animationId);
    animationId = null;
  }

  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
    resizeTimeout = null;
  }

  if (mouseMoveHandler) {
    window.removeEventListener('mousemove', mouseMoveHandler);
    mouseMoveHandler = null;
  }

  // Remove click handler
  if ((window as any).__cortexClickHandler) {
    window.removeEventListener('click', (window as any).__cortexClickHandler);
    (window as any).__cortexClickHandler = null;
  }

  // Clean up sphere click handlers
  if ((window as any).__sphereMouseDownHandler) {
    window.removeEventListener('mousedown', (window as any).__sphereMouseDownHandler);
    window.removeEventListener('mouseup', (window as any).__sphereMouseUpHandler);
    window.removeEventListener('click', (window as any).__sphereClickHandler);
    (window as any).__sphereMouseDownHandler = null;
    (window as any).__sphereMouseUpHandler = null;
    (window as any).__sphereClickHandler = null;
  }

  mouseX = 0;
  mouseY = 0;
  targetLookAtX = 0;
  targetLookAtY = 0;
}

/**
 * Get the current state
 */
export function getMajakuPrivacyBackgroundState(): MajakuPrivacyBackgroundState {
  return state;
}

/**
 * Toggle pipe visibility
 */
export function togglePipeVisibility(): boolean {
  pipesVisible = !pipesVisible;
  return pipesVisible;
}

/**
 * Get current pipe visibility state
 */
export function getPipeVisibility(): boolean {
  return pipesVisible;
}
